import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, FileText, ShoppingCart, Wrench, Calculator, ListTodo, Settings, BarChart3, Package, Truck, CreditCard, Activity, UserCheck, Building, User, MessageSquare, Shield, Crown, FileCheck, Star, Award, AlertTriangle, TrendingUp, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppStore } from '../../store';

interface SearchItem {
  id: string;
  title: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  description: string;
  type: 'page' | 'client' | 'supplier';
  aliases?: string[];
}

const GlobalSearch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Get data from store
  const { 
    clients, 
    suppliers,
    fetchClients,
    fetchSuppliers
  } = useAppStore();

  // Fetch data when search opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchSuppliers();
    }
  }, [isOpen, fetchClients, fetchSuppliers]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Define searchable pages
  const pageItems: SearchItem[] = [
    // Vista General
    { id: 'dashboard', title: 'Dashboard', path: '/', icon: <BarChart3 className="h-4 w-4" />, category: 'Vista General', description: 'Panel principal', type: 'page' },
    { id: 'analitica', title: 'Analítica', path: '/analitica', icon: <BarChart3 className="h-4 w-4" />, category: 'Vista General', description: 'Ventas, margen, cobranzas y clientes', type: 'page', aliases: ['analytics', 'análisis', 'analisis', 'métricas negocio'] },
    
    // Administración
    { id: 'clients', title: 'Clientes', path: '/clientes', icon: <Users className="h-4 w-4" />, category: 'Administración', description: 'Gestión de clientes', type: 'page' },
    { id: 'suppliers', title: 'Proveedores', path: '/proveedores', icon: <Package className="h-4 w-4" />, category: 'Administración', description: 'Gestión de proveedores', type: 'page' },
    { id: 'invoices', title: 'Facturas', path: '/facturas', icon: <FileText className="h-4 w-4" />, category: 'Administración', description: 'Facturas de clientes y proveedores', type: 'page' },
    { id: 'payments', title: 'Pagos', path: '/tesoreria', icon: <CreditCard className="h-4 w-4" />, category: 'Administración', description: 'Gestión de pagos', type: 'page' },
    
    // Producción
    { id: 'productions', title: 'Producciones', path: '/producciones', icon: <Wrench className="h-4 w-4" />, category: 'Producción', description: 'Gestión de producciones', type: 'page' },
    { id: 'samples', title: 'Muestras', path: '/muestras', icon: <Package className="h-4 w-4" />, category: 'Producción', description: 'Gestión de muestras', type: 'page' },
    { id: 'production-manager', title: 'Gestor de Producción', path: '/productionmanager', icon: <Wrench className="h-4 w-4" />, category: 'Producción', description: 'Administración de producción', type: 'page', aliases: ['manager', 'production manager', 'gestor', 'gestor de produccion', 'gestor de producción', 'pm'] },
    { id: 'production-admin', title: 'Admin de Producción', path: '/productionadmin', icon: <Settings className="h-4 w-4" />, category: 'Producción', description: 'Configuración de producción', type: 'page' },
    { id: 'production-todos', title: 'To-Do Producción', path: '/production-todos', icon: <ListTodo className="h-4 w-4" />, category: 'Producción', description: 'Tareas de producción por día', type: 'page' },
    { id: 'fichas-aprobacion', title: 'Aprobación de Fichas', path: '/fichas', icon: <FileCheck className="h-4 w-4" />, category: 'PRO', description: 'Aprobación de fichas de vendedores', type: 'page', aliases: ['fichas', 'aprobacion', 'aprobación', 'fichas aprobacion', 'fichas aprobación'] },
    
    // Ventas
    { id: 'express', title: 'Pedidos Express', path: '/express', icon: <ShoppingCart className="h-4 w-4" />, category: 'Ventas', description: 'Pedidos rápidos', type: 'page' },
    { id: 'express-stock', title: 'Stock Express', path: '/express/stock', icon: <Package className="h-4 w-4" />, category: 'Ventas', description: 'Stock de productos express', type: 'page' },
    { id: 'express-samples', title: 'Muestras Express', path: '/express/muestras', icon: <Package className="h-4 w-4" />, category: 'Ventas', description: 'Muestras express', type: 'page' },
    { id: 'cotizador', title: 'Cotizador', path: '/cotizador', icon: <Calculator className="h-4 w-4" />, category: 'Ventas', description: 'Crear cotizaciones', type: 'page' },
    { id: 'remitos', title: 'Remitos', path: '/remitos', icon: <Truck className="h-4 w-4" />, category: 'Ventas', description: 'Gestión de remitos', type: 'page' },
    { id: 'acuerdos-servicio', title: 'Acuerdos de Servicio', path: '/acuerdos-servicio', icon: <FileCheck className="h-4 w-4" />, category: 'Ventas', description: 'Contratos y acuerdos con clientes', type: 'page', aliases: ['acuerdos', 'servicio', 'contratos'] },
    { id: 'pedidos', title: 'Pedidos', path: '/pedidos', icon: <Package className="h-4 w-4" />, category: 'Ventas', description: 'Gestión de pedidos', type: 'page' },
    
    // Finanzas
    { id: 'estado-resultados', title: 'Estado de Resultados', path: '/estado-resultados', icon: <TrendingUp className="h-4 w-4" />, category: 'Finanzas', description: 'Ingresos, costos y utilidad', type: 'page', aliases: ['eerr', 'resultados', 'ingresos', 'costos'] },
    { id: 'treasury', title: 'Tesorería', path: '/tesoreria', icon: <BarChart3 className="h-4 w-4" />, category: 'Finanzas', description: 'Control financiero', type: 'page' },
    { id: 'expenses', title: 'Gastos', path: '/gastos', icon: <Activity className="h-4 w-4" />, category: 'Finanzas', description: 'Gestión de gastos', type: 'page' },
    
    // Logística
    { id: 'logistics', title: 'Logística', path: '/logistics', icon: <Truck className="h-4 w-4" />, category: 'Logística', description: 'Gestión logística', type: 'page' },
    
    // Herramientas
    { id: 'todolist', title: 'Proyectos', path: '/todolist', icon: <ListTodo className="h-4 w-4" />, category: 'Herramientas', description: 'Gestión de proyectos y tareas', type: 'page' },
    { id: 'contenido-calendario', title: 'Calendario', path: '/contenido/calendario', icon: <Calendar className="h-4 w-4" />, category: 'Contenido', description: 'Calendario de contenido por marcas', type: 'page' },
    { id: 'contenido-informes', title: 'Informes', path: '/contenido/informes', icon: <FileText className="h-4 w-4" />, category: 'Contenido', description: 'Carga del informe mensual', type: 'page' },
    { id: 'assistant', title: 'Asistente IA', path: '/asistente', icon: <Activity className="h-4 w-4" />, category: 'Herramientas', description: 'Asistente inteligente', type: 'page' },
    { id: 'chat', title: 'Chat', path: '/chat', icon: <MessageSquare className="h-4 w-4" />, category: 'Herramientas', description: 'Sistema de chat', type: 'page' },
    { id: 'reports', title: 'Reportes', path: '/reportes', icon: <BarChart3 className="h-4 w-4" />, category: 'Herramientas', description: 'Reportes y estadísticas', type: 'page' },
    { id: 'settings', title: 'Configuración', path: '/configuracion', icon: <Settings className="h-4 w-4" />, category: 'Herramientas', description: 'Configuración del sistema', type: 'page' },
    
    // Gestión (Solo Administradores)
    { id: 'user-management', title: 'Gestión de Usuarios', path: '/usuarios', icon: <UserCheck className="h-4 w-4" />, category: 'Gestión', description: 'Administrar usuarios', type: 'page' },
    { id: 'colaboradores', title: 'Colaboradores', path: '/colaboradores', icon: <Users className="h-4 w-4" />, category: 'Gestión', description: 'RRHH: sueldos, comisiones, vacaciones', type: 'page', aliases: ['rrhh', 'empleados', 'sueldos'] },
    { id: 'role-management', title: 'Gestión de Roles', path: '/roles-permisos', icon: <Users className="h-4 w-4" />, category: 'Gestión', description: 'Administrar roles', type: 'page' },
    { id: 'client-portals', title: 'Portales de Clientes', path: '/portales-clientes', icon: <Shield className="h-4 w-4" />, category: 'Gestión', description: 'Gestionar portales de clientes', type: 'page' },
    { id: 'club-productex', title: 'Club Productex®', path: '/club-productex-config', icon: <Crown className="h-4 w-4" />, category: 'Gestión', description: 'Configurar Club Productex', type: 'page' },
    { id: 'crm-seguimiento', title: 'Seguimiento CRM', path: '/seguimiento', icon: <UserCheck className="h-4 w-4" />, category: 'Gestión', description: 'Gestión de leads y oportunidades', type: 'page' },
    { id: 'archived', title: 'Archivados', path: '/archivados', icon: <Package className="h-4 w-4" />, category: 'Gestión', description: 'Producciones y muestras archivadas', type: 'page' },
    { id: 'satisfaction-surveys', title: 'Encuestas de Satisfacción', path: '/encuestas-satisfaccion', icon: <Star className="h-4 w-4" />, category: 'Gestión', description: 'Encuestas de satisfacción al cliente', type: 'page', aliases: ['encuestas', 'satisfaccion', 'satisfacción'] },
    { id: 'claims-admin', title: 'Reclamos', path: '/reclamos', icon: <AlertTriangle className="h-4 w-4" />, category: 'Gestión', description: 'Ver y gestionar reclamos', type: 'page', aliases: ['reclamos', 'formulario reclamos'] },
    { id: 'claims-generator', title: 'Generar Reclamo', path: '/generar-reclamo', icon: <AlertTriangle className="h-4 w-4" />, category: 'Ventas', description: 'Generar link de formulario de reclamo', type: 'page', aliases: ['generar reclamo', 'formulario reclamo'] },
    { id: 'mis-puntos', title: 'Mis Puntos', path: '/puntos-colaboradores', icon: <Award className="h-4 w-4" />, category: 'Herramientas', description: 'Ver y canjear puntos de colaborador', type: 'page', aliases: ['puntos', 'mis puntos', 'colaboradores'] },
    { id: 'puntos-colaboradores-config', title: 'Puntos Colaboradores', path: '/puntos-colaboradores-config', icon: <Award className="h-4 w-4" />, category: 'Gestión', description: 'Configurar puntos y recompensas', type: 'page', aliases: ['puntos colaboradores', 'config puntos'] },
    
    // Actividad
    { id: 'activity', title: 'Actividad', path: '/actividad', icon: <Activity className="h-4 w-4" />, category: 'Seguimiento', description: 'Registro de actividad', type: 'page' },
    { id: 'notifications', title: 'Notificaciones', path: '/notificaciones', icon: <Activity className="h-4 w-4" />, category: 'Seguimiento', description: 'Centro de notificaciones', type: 'page' },
  ];

  // Generate dynamic search items from data
  const generateDynamicItems = (): SearchItem[] => {
    const dynamicItems: SearchItem[] = [];

    // Add clients
    clients.forEach(client => {
      dynamicItems.push({
        id: `client-${client.id}`,
        title: client.name,
        path: `/clientes/${client.id}`,
        icon: <User className="h-4 w-4" />,
        category: 'Clientes',
        description: `Cliente: ${client.email || 'Sin email'} - ${client.phone || 'Sin teléfono'}`,
        type: 'client'
      });
    });

    // Add suppliers
    suppliers.forEach(supplier => {
      dynamicItems.push({
        id: `supplier-${supplier.id}`,
        title: supplier.name,
        path: `/proveedores/${supplier.id}`,
        icon: <Building className="h-4 w-4" />,
        category: 'Proveedores',
        description: `Proveedor: ${supplier.email || 'Sin email'} - ${supplier.phone || 'Sin teléfono'}`,
        type: 'supplier'
      });
    });

    return dynamicItems;
  };

  // Combine page items with dynamic items
  const allSearchItems = [...pageItems, ...generateDynamicItems()];

  // Filter items based on query
  const q = query.toLowerCase();
  const filteredItems = allSearchItems.filter(item =>
    item.title.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    item.category.toLowerCase().includes(q) ||
    (item.aliases && item.aliases.some(a => a.toLowerCase().includes(q)))
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isAdmin) return;
      
      // Open search with Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      if (!isOpen) return;

      // Close with Escape
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
        return;
      }

      // Navigate with arrow keys
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredItems.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredItems.length - 1
        );
        return;
      }

      // Select with Enter
      if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelectItem(filteredItems[selectedIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin, isOpen, selectedIndex, filteredItems]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelectItem = (item: SearchItem) => {
    navigate(item.path);
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      setIsOpen(false);
      setQuery('');
      setSelectedIndex(0);
    }
  };

  // Don't render anything if user is not admin
  if (!isAdmin) return null;

  return (
    <>
      {/* Search Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="hidden md:flex items-center space-x-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="hidden lg:inline">Buscar</span>
        <kbd className="hidden lg:inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded">
          {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
        </kbd>
      </button>

      {/* Search Modal */}
      {isOpen && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4"
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          
          {/* Modal */}
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            {/* Search Input */}
            <div className="flex items-center space-x-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar páginas, clientes, proveedores..."
                className="flex-1 text-sm bg-transparent border-none outline-none placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white"
              />
              <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto">
              {filteredItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No se encontraron resultados</p>
                  <p className="text-sm">Intenta con otros términos</p>
                </div>
              ) : (
                <div className="p-2">
                  {filteredItems.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelectItem(item)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-md text-left transition-colors ${
                        index === selectedIndex
                          ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      <div className={`p-2 rounded-md ${
                        index === selectedIndex
                          ? 'bg-indigo-100 dark:bg-indigo-800'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        {item.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            item.type === 'page' 
                              ? 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20'
                              : item.type === 'client'
                              ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20'
                              : item.type === 'supplier'
                              ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/20'
                              : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700'
                          }`}>
                            {item.category}
                          </span>
                          {item.type !== 'page' && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded">
                              {item.type === 'client' ? 'Cliente' :
                               item.type === 'supplier' ? 'Proveedor' : 'Elemento'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center justify-between">
                <span>Usar ↑ ↓ para navegar</span>
                <span>Enter para seleccionar</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;